import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { parsingRows } from './worker';
import { getColumnMapping } from '../ContributionMapping/worker';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import TrueImpactModal, {
  modalHeightAutoStyles,
  modelExtraRadiusStyles,
} from 'shared/design-system/components/TrueImpactModal';
import CellUpdateModalContent, {
  ContributionModalOpen,
} from './CellUpdateModalContent';
import { InvestmentColumnKeys, investmentPrettifyText } from '../utils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TableCellDelete from './TableCellDelete';
import ShowErrorIcon from './ShowErrorIcon';

interface ContributionMappingTableRowProps {
  index: number;
  row: string[];
  isLoadingValidation: boolean;
  columns: ReturnType<typeof getColumnMapping>;
  finalData: ReturnType<typeof parsingRows>;
  handleDeleteRow: (rowIndex: number) => unknown;
  handleUpdateRow: (
    rowIndex: number,
    columnIndex: number,
    systemKey: InvestmentColumnKeys,
    value: string,
  ) => unknown;
}

const StyledRow = styled(TableRow)<{ fullRowError: boolean }>`
  ${({ fullRowError }) =>
    fullRowError ? `background-color: ${COLORS.criticalBackground};` : ''}
  .investment-data-cell-wrapper {
    .investment-data-cell {
      display: flex;
      align-items: center;
    }
  }
  .cell-warning {
    background-color: ${COLORS.warningBackground};
  }
  .cell-error {
    background-color: ${COLORS.criticalBackground};
    .error-icon {
      margin-right: ${SPACERS.extraTiny};
    }
  }
  .cursor {
    transition: background-color 0.3s;
    &:hover {
      background-color: ${COLORS.ctaPrimaryBackground};
    }
  }
`;

const ContributionMappingTableRow: React.FC<ContributionMappingTableRowProps> = ({
  index,
  row,
  columns,
  finalData,
  isLoadingValidation,
  handleUpdateRow,
  handleDeleteRow,
}) => {
  const [open, setOpen] = useState<ContributionModalOpen | undefined>(
    undefined,
  );
  const validation = finalData.failedRowsIndexes[index];

  const handleClose = useCallback(() => {
    setOpen(undefined);
  }, [setOpen]);

  return (
    <>
      <StyledRow fullRowError={validation === true}>
        {row.map((r, i) => {
          const systemKey = columns.systemColumns[i - 1];
          const keyString = systemKey + '-' + i;
          if (i === 0) {
            return (
              <TableCellDelete
                key={keyString}
                text={r}
                onClick={() => handleDeleteRow(index)}
              />
            );
          }
          const handleClick = () => {
            systemKey &&
              setOpen({
                rowIndex: index,
                columnIndex: i - 1,
                value: r,
                systemKey,
              });
          };
          const isError = validation && validation !== true && validation[i];
          return (
            <TableCell
              key={keyString}
              onClick={systemKey ? handleClick : undefined}
              className={`investment-data-cell-wrapper${
                isError ? ' cell-error' : ''
              }${systemKey ? ' cursor' : ''}${
                isLoadingValidation ? ' cell-warning' : ''
              }`}
            >
              <div className="investment-data-cell">
                {isError && (
                  <ShowErrorIcon systemKey={systemKey} className="error-icon" />
                )}
                {investmentPrettifyText(systemKey, r)}
              </div>
            </TableCell>
          );
        })}
      </StyledRow>
      {open && (
        <TrueImpactModal
          open={Boolean(open)}
          maxWidth="700px"
          hideFooter={true}
          onClose={handleClose}
          addStyles={modalHeightAutoStyles + modelExtraRadiusStyles}
        >
          <CellUpdateModalContent
            row={row}
            value={open}
            columns={columns}
            handleClose={handleClose}
            handleUpdateRow={handleUpdateRow}
          />
        </TrueImpactModal>
      )}
    </>
  );
};

export default ContributionMappingTableRow;
